import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { ENVIRONMENT, MlaBorrowerAccountCreationResponse } from '@ld.consumer-portal/models';
import { AuthService, LoggingService, ThemeService } from '@ld.consumer-portal/utils';
import { firstValueFrom, timeout } from 'rxjs';

import {
    BorrowerVerificationResponse,
    MlaAuthState,
    MlaBorrowerInfo,
    MlaSsoBorrower,
    MlaSsoData,
    MlaSsoResponse,
    MlaStoredSession,
    MlaUserStatusResponse,
} from '@ld.consumer-portal/models';

@Injectable({
  providedIn: 'root',
})
export class MlaAuthService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly authService = inject(AuthService);
  private readonly loggingService = inject(LoggingService);
  private readonly themeService = inject(ThemeService);

  private readonly STORAGE_KEYS = {
    epsToken: 'eps_token', // Token from /authentication/token - used for MLA API auth
    mlaSsoToken: 'mla_token', // Token from /session/sso - used for CP SSO exchange
    userId: 'mla_userId',
    borrowerId: 'mla_borrowerId',
    cpBorrowerId: 'mla_cpBorrowerId',
    loanId: 'mla_loanId',
    channel: 'mla_channel',
    profileName: 'mla_profileName',
    dashboardToken: 'mla_dashboardToken',
    borrowers: 'mla_borrowers', // Temporary storage for multiple borrowers
    selectedBorrower: 'mla_selectedBorrower',
    userExistsInMla: 'mla_user_exists', // Whether user exists in MLA (persisted for MFA flow)
  } as const;

  readonly authState = signal<MlaAuthState>('logout');
  readonly isAuthenticated = signal(false);

  readonly userExistsInMla = signal(sessionStorage.getItem('mla_user_exists') !== 'false');

  readonly hasEpsAccount = signal(true);

  private userStatusFirstName = '';
  private userStatusLastName = '';
  private userStatusEntraUserId = '';
  private userStatusLoaded = false;

  private get mlaApiUrl(): string {
    return this.env.appSettings.mlaApiUrl || '';
  }

  get bearerToken(): string {
    const token = sessionStorage.getItem(this.STORAGE_KEYS.epsToken);
    return token ? `Bearer ${token}` : '';
  }

  get userId(): string | null {
    return sessionStorage.getItem(this.STORAGE_KEYS.userId);
  }

  get borrowerId(): string | null {
    return sessionStorage.getItem(this.STORAGE_KEYS.borrowerId);
  }

  get loanId(): string | null {
    return sessionStorage.getItem(this.STORAGE_KEYS.loanId);
  }

  get loanChannel(): string | null {
    return sessionStorage.getItem(this.STORAGE_KEYS.channel);
  }

  get dashboardToken(): string | null {
    return sessionStorage.getItem(this.STORAGE_KEYS.dashboardToken);
  }

  private buildHostInfo(): string {
    const partnerId = this.themeService.getHostInfoPartnerId();
    const host = window.location.origin;
    try {
      return btoa(JSON.stringify({ partnerId, host }));
    } catch {
      return '';
    }
  }

  async authenticateB2CUser(b2cIdToken: string, email: string): Promise<MlaSsoData | null> {

    if (!this.userExistsInMla()) {
      return null;
    }

    if (!b2cIdToken || !email) {
      this.loggingService.logWarning('[MlaAuthService] Missing b2cIdToken or email for B2C authentication');
      return null;
    }

    const existingToken = sessionStorage.getItem(this.STORAGE_KEYS.epsToken);
    const existingUserId = sessionStorage.getItem(this.STORAGE_KEYS.userId);
    if (existingToken && existingUserId) {
      return {
        token: existingToken,
        userId: existingUserId,
        borrowers: this.getStoredBorrowers(),
      } as MlaSsoData;
    }

    const url = `${this.mlaApiUrl}api/v1/authentication/token`;

    const accessToken = this.authService.getToken();
    const authProvider = this.env.appSettings.useCustomerIdentityAuth ? 'Entra' : 'CP2';


    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: '*/*',
      Authorization: `Bearer ${accessToken || b2cIdToken}`,
      B2CToken: b2cIdToken,
      'authorization-provider': authProvider,
      ...(this.env.appSettings.useCustomerIdentityAuth ? { 'X-Partner-Id': this.themeService.getXPartnerId() } : {}),
    };

    const body: { email: string } = { email };

    try {
      const response = await firstValueFrom(this.http.post<MlaSsoResponse>(url, body, { headers }));

      if (response?.success && response.data) {
        this.storeSession(response.data);
        this.authState.set('sso-resolved');
        this.isAuthenticated.set(true);
        return response.data;
      } else {
        this.loggingService.logWarning('[MlaAuthService] B2C auth unsuccessful', {
          success: String(response?.success),
        });
        return null;
      }
    } catch (error: unknown) {
      this.loggingService.logException(error, '[MlaAuthService] B2C auth error');
      return null;
    }
  }

  private resolveRequiredNames(
    firstName: string,
    lastName: string,
    context: string,
    useUserStatusFallback = true,
  ): { firstName: string; lastName: string } | null {
    const resolvedFirstName =
      (firstName || '').trim() || (useUserStatusFallback ? (this.userStatusFirstName || '').trim() : '');
    const resolvedLastName =
      (lastName || '').trim() || (useUserStatusFallback ? (this.userStatusLastName || '').trim() : '');

    if (!resolvedFirstName || !resolvedLastName) {
      this.loggingService.logWarning(`[MlaAuthService] Missing first or last name for ${context}`, {
        hasFirstName: String(!!resolvedFirstName),
        hasLastName: String(!!resolvedLastName),
      });
      return null;
    }

    return { firstName: resolvedFirstName, lastName: resolvedLastName };
  }

  async createEpsAccountForEntraUser(
    b2cIdToken: string,
    email: string,
    firstName: string,
    lastName: string,
    externalUserId: string,
  ): Promise<{ userId: string; token: string } | null> {
    if (!b2cIdToken || !email || !externalUserId) {
      this.loggingService.logWarning('[MlaAuthService] Missing required parameters for createEpsAccountForEntraUser');
      return null;
    }

    const names = this.resolveRequiredNames(firstName, lastName, 'createEpsAccountForEntraUser');
    if (!names) {
      return null;
    }

    const url = `${this.mlaApiUrl}api/v1/user/azure`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: '*/*',
      B2CToken: b2cIdToken,
      'authorization-provider': this.env.appSettings.useCustomerIdentityAuth ? 'Entra' : 'CP2',
      ...(this.env.appSettings.useCustomerIdentityAuth ? { 'X-Partner-Id': this.themeService.getXPartnerId() } : {}),
    };

    const body = {
      adB2CUserId: externalUserId,
      email,
      firstName: names.firstName,
      lastName: names.lastName,
    };

    try {
      const response = await firstValueFrom(
        this.http.post<{ success?: boolean; data?: MlaBorrowerAccountCreationResponse }>(url, body, { headers }),
      );

      if (response?.success && response.data) {
        const accountData = response.data as unknown as { userId: string; token: string };
        if (accountData.token) {
          sessionStorage.setItem(this.STORAGE_KEYS.epsToken, accountData.token);
        }
        if (accountData.userId) {
          sessionStorage.setItem(this.STORAGE_KEYS.userId, accountData.userId);
        }
        return accountData;
      } else {
        this.loggingService.logWarning('[MlaAuthService] EPS account creation unsuccessful', {
          success: String(response?.success),
        });
        return null;
      }
    } catch (error: unknown) {
      this.loggingService.logException(error, '[MlaAuthService] EPS account creation error');
      return null;
    }
  }

  async registerUserInMla(
    b2cIdToken: string,
    email: string,
    firstName: string,
    lastName: string,
    adB2CUserId: string,
  ): Promise<MlaBorrowerAccountCreationResponse | null> {
    if (!this.userExistsInMla()) {
      return null;
    }

    if (!b2cIdToken || !email || !adB2CUserId) {
      this.loggingService.logWarning('[MlaAuthService] Missing required parameters for registerUserInMla');
      return null;
    }

    const names = this.resolveRequiredNames(firstName, lastName, 'registerUserInMla');
    if (!names) {
      return null;
    }

    const url = `${this.mlaApiUrl}api/v1/user/azure`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: '*/*',
      B2CToken: b2cIdToken,
      'authorization-provider': this.env.appSettings.useCustomerIdentityAuth ? 'Entra' : 'CP2',
      ...(this.env.appSettings.useCustomerIdentityAuth ? { 'X-Partner-Id': this.themeService.getXPartnerId() } : {}),
    };

    const body = {
      adB2CUserId,
      email,
      firstName: names.firstName,
      lastName: names.lastName,
    };

    try {
      const response = await firstValueFrom(
        this.http.post<{ success?: boolean; data?: MlaBorrowerAccountCreationResponse }>(url, body, { headers }),
      );

      if (response?.success) {
        return response.data ?? null;
      } else {
        this.loggingService.logWarning('[MlaAuthService] MLA registration unsuccessful', {
          success: String(response?.success),
        });
        return null;
      }
    } catch (error: unknown) {
      this.loggingService.logException(error, '[MlaAuthService] MLA registration error');
      return null;
    }
  }

  async createAzureUserForMigration(
    email: string,
    firstName: string,
    lastName: string,
    externalUserId: string,
    activationToken?: string,
  ): Promise<{ userId: string; token: string } | null> {
    if (!email || !externalUserId) {
      this.loggingService.logWarning('[MlaAuthService] Missing required parameters for createAzureUserForMigration');
      return null;
    }

    const names = this.resolveRequiredNames(firstName, lastName, 'createAzureUserForMigration');
    if (!names) {
      return null;
    }

    const url = `${this.mlaApiUrl}api/v1/user/azure`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: '*/*',
      'authorization-provider': this.env.appSettings.useCustomerIdentityAuth ? 'Entra' : 'CP2',
      ...(this.env.appSettings.useCustomerIdentityAuth ? { 'X-Partner-Id': this.themeService.getXPartnerId() } : {}),
      ...(activationToken ? { 'x-activation-token': activationToken } : {}),
    };

    const body = {
      adB2CUserId: externalUserId,
      email,
      firstName: names.firstName,
      lastName: names.lastName,
    };

    try {
      const response = await firstValueFrom(
        this.http.post<{ success?: boolean; data?: MlaBorrowerAccountCreationResponse }>(url, body, { headers }),
      );

      if (response?.success && response.data) {
        const accountData = response.data as unknown as { userId: string; token: string };
        return accountData;
      } else {
        this.loggingService.logWarning('[MlaAuthService] Azure user creation for migration unsuccessful', {
          success: String(response?.success),
        });
        return null;
      }
    } catch (error: unknown) {
      this.loggingService.logException(error, '[MlaAuthService] Azure user creation for migration error');
      return null;
    }
  }

  async createCoBorrowerAccount(
    email: string,
    firstName: string,
    lastName: string,
    password: string,
    epsToken: string,
  ): Promise<{ userId: string; token: string } | null> {
    if (!email || !password || !epsToken) {
      this.loggingService.logWarning('[MlaAuthService] Missing required parameters for createCoBorrowerAccount');
      return null;
    }

    const names = this.resolveRequiredNames(firstName, lastName, 'createCoBorrowerAccount', false);
    if (!names) {
      return null;
    }

    const url = `${this.mlaApiUrl}api/v1/user/azure`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: '*/*',
      Authorization: `Bearer ${epsToken}`,
      'authorization-provider': 'eps',
      ...(this.env.appSettings.useCustomerIdentityAuth ? { 'X-Partner-Id': this.themeService.getXPartnerId() } : {}),
    };

    const body = {
      email,
      firstName: names.firstName,
      lastName: names.lastName,
      password,
    };

    try {
      const response = await firstValueFrom(
        this.http.post<{ success?: boolean; data?: MlaBorrowerAccountCreationResponse }>(url, body, { headers }),
      );

      if (response?.success && response.data) {
        const accountData = response.data as unknown as { userId: string; token: string };
        if (accountData.token) {
          sessionStorage.setItem(this.STORAGE_KEYS.epsToken, accountData.token);
        }
        if (accountData.userId) {
          sessionStorage.setItem(this.STORAGE_KEYS.userId, accountData.userId);
        }
        return accountData;
      } else {
        this.loggingService.logWarning('[MlaAuthService] Co-borrower account creation unsuccessful', {
          success: String(response?.success),
        });
        return null;
      }
    } catch (error: unknown) {
      this.loggingService.logException(error, '[MlaAuthService] Co-borrower account creation error');
      return null;
    }
  }

  async createAccountWithUserGuid(
    email: string,
    firstName: string,
    lastName: string,
    password: string,
    activationToken: string,
  ): Promise<{ userId: string; token: string } | null> {
    if (!email || !password || !activationToken) {
      this.loggingService.logWarning('[MlaAuthService] Missing required parameters for createAccountWithUserGuid');
      return null;
    }

    const url = `${this.mlaApiUrl}api/v1/user/azure`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: '*/*',
      'x-activation-token': activationToken,
      ...(this.env.appSettings.useCustomerIdentityAuth ? { 'X-Partner-Id': this.themeService.getXPartnerId() } : {}),
    };

    const body = {
      externalUserId: crypto.randomUUID(),
      email,
      firstName,
      lastName,
      password,
    };

    try {
      const response = await firstValueFrom(
        this.http.post<{ success?: boolean; data?: MlaBorrowerAccountCreationResponse }>(url, body, { headers }),
      );

      if (response?.success && response.data) {
        const accountData = response.data as unknown as { userId: string; token: string };
        if (accountData.token) {
          sessionStorage.setItem(this.STORAGE_KEYS.epsToken, accountData.token);
        }
        if (accountData.userId) {
          sessionStorage.setItem(this.STORAGE_KEYS.userId, accountData.userId);
        }
        return accountData;
      } else {
        this.loggingService.logWarning('[MlaAuthService] Account creation with userGuid unsuccessful', {
          success: String(response?.success),
        });
        return null;
      }
    } catch (error: unknown) {
      this.loggingService.logException(error, '[MlaAuthService] Account creation with userGuid error');
      return null;
    }
  }

  private readonly LOCAL_STORAGE_VISITOR_KEY = 'Session_MacVisitorId';

  generateVisitorId(): string {
    const visitorId = localStorage.getItem(this.LOCAL_STORAGE_VISITOR_KEY);
    if (visitorId) {
      return visitorId;
    }

    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15).toUpperCase();
    const newVisitorId = `${timestamp}${random}`;
    localStorage.setItem(this.LOCAL_STORAGE_VISITOR_KEY, newVisitorId);
    return newVisitorId;
  }

  async ssoLogin(ssoToken: string): Promise<MlaSsoData | null> {
    if (!ssoToken) {
      this.loggingService.logWarning('MlaAuthService: No ssoToken provided for SSO login');
      return null;
    }

    const url = `${this.mlaApiUrl}api/v1/session/sso`;

    const headers: Record<string, string> = {
      HostInfo: this.buildHostInfo(),
    };

    try {
      const response = await firstValueFrom(this.http.get<MlaSsoResponse>(url, { headers }));

      if (response?.success && response.data) {
        this.storeSession(response.data);
        this.authState.set('sso-resolved');
        this.isAuthenticated.set(true);
        return response.data;
      } else {
        this.loggingService.logWarning('MlaAuthService: SSO login unsuccessful', {
          success: String(response?.success),
        });
        return null;
      }
    } catch (error) {
      this.loggingService.logException(error, 'MlaAuthService: SSO login error');
      return null;
    }
  }

  private storeSession(data: MlaSsoData): void {
    if (data.token) {
      sessionStorage.setItem(this.STORAGE_KEYS.epsToken, data.token);
    }
    if (data.userId) {
      sessionStorage.setItem(this.STORAGE_KEYS.userId, data.userId);
    }
    if (data.visitorId) {
      localStorage.setItem(this.LOCAL_STORAGE_VISITOR_KEY, data.visitorId);
    }

    const alreadySelected = sessionStorage.getItem(this.STORAGE_KEYS.selectedBorrower);

    if (data.borrowers && data.borrowers.length > 0) {
      sessionStorage.setItem(this.STORAGE_KEYS.borrowers, JSON.stringify(data.borrowers));

      if (!alreadySelected && data.borrowers.length === 1) {
        const borrower = data.borrowers[0];
        sessionStorage.setItem(this.STORAGE_KEYS.borrowerId, borrower.borrowerId);
        sessionStorage.setItem(this.STORAGE_KEYS.profileName, `${borrower.firstName} ${borrower.lastName}`);
        sessionStorage.setItem(this.STORAGE_KEYS.dashboardToken, borrower.borrowerToken);
        sessionStorage.setItem(this.STORAGE_KEYS.selectedBorrower, JSON.stringify(borrower));
      }
      // If a borrower was already selected or multiple borrowers exist, preserve existing selection
    }
  }

  getStoredSession(): MlaStoredSession | null {
    const token = sessionStorage.getItem(this.STORAGE_KEYS.epsToken);
    const userId = sessionStorage.getItem(this.STORAGE_KEYS.userId);

    if (!token || !userId) {
      return null;
    }

    return {
      token,
      userId,
      borrowerId: sessionStorage.getItem(this.STORAGE_KEYS.borrowerId) || '',
      loanId: sessionStorage.getItem(this.STORAGE_KEYS.loanId) || undefined,
      channel: sessionStorage.getItem(this.STORAGE_KEYS.channel) || undefined,
      profileName: sessionStorage.getItem(this.STORAGE_KEYS.profileName) || undefined,
      dashboardToken: sessionStorage.getItem(this.STORAGE_KEYS.dashboardToken) || undefined,
    };
  }

  logout(): void {
    Object.values(this.STORAGE_KEYS).forEach((key) => {
      sessionStorage.removeItem(key);
    });

    localStorage.removeItem(this.LOCAL_STORAGE_VISITOR_KEY);

    this.authState.set('logout');
    this.isAuthenticated.set(false);
    this.setUserExistsInMla(true);
    this.hasEpsAccount.set(true);
    this.userStatusFirstName = '';
    this.userStatusLastName = '';
    this.userStatusEntraUserId = '';
    this.userStatusLoaded = false;
  }

  private setUserExistsInMla(exists: boolean): void {
    this.userExistsInMla.set(exists);
    if (exists) {
      sessionStorage.removeItem(this.STORAGE_KEYS.userExistsInMla);
    } else {
      sessionStorage.setItem(this.STORAGE_KEYS.userExistsInMla, 'false');
    }
  }

  hasValidSession(): boolean {
    return !!this.bearerToken && !!this.userId;
  }

  setLoanChannel(channel: 'Direct' | 'Retail'): void {
    sessionStorage.setItem(this.STORAGE_KEYS.channel, channel);
  }

  setLoanId(loanId: string): void {
    sessionStorage.setItem(this.STORAGE_KEYS.loanId, loanId);
  }

  setDashboardToken(token: string): void {
    sessionStorage.setItem(this.STORAGE_KEYS.dashboardToken, token);
  }

  needsBorrowerVerification(): boolean {
    const borrowersJson = sessionStorage.getItem(this.STORAGE_KEYS.borrowers);
    if (!borrowersJson) return false;

    try {
      const borrowers: MlaSsoBorrower[] = JSON.parse(borrowersJson);
      if (!Array.isArray(borrowers)) return false;

      const hasBorrowerId = !!sessionStorage.getItem(this.STORAGE_KEYS.borrowerId);
      if (hasBorrowerId) return false;

      return borrowers.length >= 2;
    } catch {
      return false;
    }
  }

  getStoredBorrowers(): MlaSsoBorrower[] {
    const borrowersJson = sessionStorage.getItem(this.STORAGE_KEYS.borrowers);
    if (!borrowersJson) return [];

    try {
      return JSON.parse(borrowersJson);
    } catch {
      return [];
    }
  }

  storeBorrowersFromBorrowerInfo(borrowers: MlaBorrowerInfo[]): void {
    if (!borrowers || borrowers.length === 0) {
      return;
    }

    const ssoBorrowers: MlaSsoBorrower[] = borrowers.map((b) => ({
      borrowerId: b.borrowerIds?.[0] || b.borrowerInfoId,
      cpBorrowerId: b.cpBorrowerIds?.[0],
      firstName: b.firstName,
      lastName: b.lastName,
      borrowerToken: b.borrowerToken,
      isPrimary: b.isPrimary,
      ssnVerify: b.ssnVerify,
      mostRecentPhoneNumber: b.mostRecentPhoneNumber,
      phoneNumbers: b.phoneNumbers,
    }));

    sessionStorage.setItem(this.STORAGE_KEYS.borrowers, JSON.stringify(ssoBorrowers));

    if (ssoBorrowers.length === 1) {
      const borrower = ssoBorrowers[0];
      sessionStorage.setItem(this.STORAGE_KEYS.borrowerId, borrower.borrowerId);
      sessionStorage.setItem(this.STORAGE_KEYS.cpBorrowerId, borrower.cpBorrowerId || '');
      sessionStorage.setItem(this.STORAGE_KEYS.profileName, `${borrower.firstName} ${borrower.lastName}`);
      sessionStorage.setItem(this.STORAGE_KEYS.dashboardToken, borrower.borrowerToken);
      sessionStorage.setItem(this.STORAGE_KEYS.selectedBorrower, JSON.stringify(borrower));
    }
  }

  public getMlaPrimaryBorrowerPhoneNumbers(): string[] {
    const borrowersJson = sessionStorage.getItem(this.STORAGE_KEYS.borrowers);
    if (!borrowersJson) return [];
    const borrowers: MlaSsoBorrower[] = JSON.parse(borrowersJson);
    const phoneNumbers = this.getUniquePhoneNumbers(borrowers);
    return phoneNumbers;
  }

  public getSelectedBorrowerPhoneNumbers(): string[] {
    const snapshot = this.getSelectedBorrowerSnapshot();
    if (snapshot) {
      return this.getUniquePhoneNumbers([snapshot]);
    }
    return this.getMlaPrimaryBorrowerPhoneNumbers();
  }

  public getSelectedBorrowerName(): { firstName: string; lastName: string } | null {
    const snapshot = this.getSelectedBorrowerSnapshot();
    if (snapshot) {
      return { firstName: snapshot.firstName, lastName: snapshot.lastName };
    }
    return null;
  }

  public getSelectedBorrowerId(): string | null {
    const snapshot = this.getSelectedBorrowerSnapshot();
    if (snapshot) {
      return snapshot.cpBorrowerId ?? snapshot.borrowerId ?? null;
    }
    return null;
  }

  private getSelectedBorrowerSnapshot(): MlaSsoBorrower | null {
    const json = sessionStorage.getItem(this.STORAGE_KEYS.selectedBorrower);
    if (!json) return null;
    try {
      return JSON.parse(json) as MlaSsoBorrower;
    } catch {
      return null;
    }
  }

  getUniquePhoneNumbers(borrowers: MlaSsoBorrower[]): string[] {
    const all = borrowers.flatMap((b) => [
      ...(b.phoneNumbers ?? []),
      ...(b.mostRecentPhoneNumber ? [b.mostRecentPhoneNumber] : []),
    ]);

    const cleaned = all.map((p) => p?.trim()).filter((p): p is string => Boolean(p && p.length > 0));

    return Array.from(new Set(cleaned));
  }

  private decodeBorrowerToken(token: string): string[] {
    if (!token) return [];
    try {
      const parsed = JSON.parse(atob(token));
      return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
    } catch {
      return [];
    }
  }

  getActiveBorrowerIds(): string[] {
    const token = this.dashboardToken;
    return token ? this.decodeBorrowerToken(token) : [];
  }

  selectBorrower(borrowerToken: string): void {
    const borrowers = this.getStoredBorrowers();
    const inputBorrowerIds = this.decodeBorrowerToken(borrowerToken);

    const selected = borrowers.find((b) => {
      if (b.borrowerToken === borrowerToken) return true;
      const storedIds = [...this.decodeBorrowerToken(b.borrowerToken), ...(b.borrowerIds ?? [])];
      return inputBorrowerIds.some((id) => storedIds.includes(id));
    });

    if (selected) {
      sessionStorage.setItem(this.STORAGE_KEYS.borrowerId, selected.borrowerId);
      sessionStorage.setItem(this.STORAGE_KEYS.profileName, `${selected.firstName} ${selected.lastName}`);
      sessionStorage.setItem(this.STORAGE_KEYS.dashboardToken, selected.borrowerToken);
      sessionStorage.setItem(this.STORAGE_KEYS.selectedBorrower, JSON.stringify(selected));
    }
  }

  async verifyBorrowerWithSsn(borrowerToken: string, lastFourSsn: string): Promise<BorrowerVerificationResponse> {
    const url = `${this.mlaApiUrl}api/v1/borrower/verify`;

    const params = new URLSearchParams();
    params.append('borrowerToken', borrowerToken);
    params.append('lastFourSsn', lastFourSsn);

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; data: BorrowerVerificationResponse }>(`${url}?${params.toString()}`, {}),
      );

      const verificationData = response?.data;
      if (verificationData?.isVerified) {
        this.selectBorrower(borrowerToken);
      }

      return verificationData;
    } catch (error) {
      this.loggingService.logException(error, '[MlaAuthService] Borrower verification failed:');
      return {
        success: false,
        isVerified: false,
        borrowerToken: '',
        message: 'Verification failed. Please try again.',
      };
    }
  }

  async getUserStatus(email: string, partnerId = 'loandepot'): Promise<MlaUserStatusResponse> {
    const url = `${this.mlaApiUrl}api/v1/user/status?email=${encodeURIComponent(email)}&distinctId=${encodeURIComponent(partnerId)}`;

    const response = await firstValueFrom(this.http.get<MlaUserStatusResponse>(url).pipe(timeout(3000)));

    if (response?.success && response.data?.status === 'NotExist') {
      this.setUserExistsInMla(false);
    } else {
      this.setUserExistsInMla(true);
    }

    this.hasEpsAccount.set(!!response?.data?.epsUser);

    this.userStatusFirstName = response?.data?.firstName ?? '';
    this.userStatusLastName = response?.data?.lastName ?? '';
    this.userStatusEntraUserId = response?.data?.entraUser?.userId ?? '';
    this.userStatusLoaded = true;

    return response;
  }

  private resolveStatusPartnerId(): string {
    return this.themeService.isJvThemeActive() ? this.themeService.getXPartnerId() : 'loandepot';
  }

  userHasEpsAccount(): boolean {
    return this.hasEpsAccount();
  }

  getUserStatusFirstName(): string {
    return this.userStatusFirstName;
  }

  getUserStatusLastName(): string {
    return this.userStatusLastName;
  }

  getUserStatusEntraUserId(): string {
    return this.userStatusEntraUserId;
  }

  async authenticateOrRegisterB2CUser(b2cIdToken: string, email: string): Promise<MlaSsoData | null> {
    if (this.hasEpsAccount()) {
      const result = await this.authenticateB2CUser(b2cIdToken, email);
      if (result) {
        return result;
      }

      const userId = this.authService.getUserId() || this.userStatusEntraUserId;
      if (!userId) {
        this.loggingService.logWarning(
          '[MlaAuthService] authenticateB2CUser failed and no userId available for fallback',
        );
        return null;
      }

      this.loggingService.logWarning(
        '[MlaAuthService] authenticateB2CUser failed, falling back to createEpsAccountForEntraUser',
      );

      const tokenNames = this.authService.extractNamesFromB2cToken();
      const firstName = this.userStatusFirstName || tokenNames.firstName;
      const lastName = this.userStatusLastName || tokenNames.lastName;

      const azureResult = await this.createEpsAccountForEntraUser(
        b2cIdToken, email, firstName, lastName, userId,
      );
      if (azureResult) {
        this.loggingService.logInfo(
          `[MlaAuthService] Azure user created/linked via fallback: userId=${azureResult.userId}`,
        );
        sessionStorage.removeItem(this.STORAGE_KEYS.epsToken);
        sessionStorage.removeItem(this.STORAGE_KEYS.userId);
        return this.authenticateB2CUser(b2cIdToken, email);
      }

      this.loggingService.logWarning('[MlaAuthService] createEpsAccountForEntraUser fallback also failed');
      return null;
    }

    this.loggingService.logWarning('[MlaAuthService] No EPS account found, skipping MLA auth');
    return null;
  }

  async setUserMigrating(customerGuid: string, email: string, partnerId = 'loandepot'): Promise<unknown> {
    const url = `${this.mlaApiUrl}api/v1/user/migrating`;

    const body = {
      customerGuid,
      email,
      partnerId,
    };

    return firstValueFrom(this.http.post<unknown>(url, body));
  }

  private static readonly PLACEHOLDER_USER_ID = 'ffffffff-ffff-ffff-ffff-ffffffffffff';

  async getSsoTokenForCp(loanId: string | undefined, borrowerId: string): Promise<string | null> {
    if (!this.userExistsInMla()) {
      return null;
    }

    const token = this.bearerToken;
    if (!token) {
      this.loggingService.logWarning('[MlaAuthService] No MLA token available for SSO token generation');
      return null;
    }

    const mlaUserId = this.userId;
    if (!mlaUserId || mlaUserId === MlaAuthService.PLACEHOLDER_USER_ID) {
      this.loggingService.logWarning('[MlaAuthService] Invalid MLA userId for SSO token generation, proceeding without userId');
    }

    this.loggingService.logInfo(`[MlaAuthService] getSsoTokenForCp called for loanId=${loanId}, userId=${mlaUserId}`);

    const params = new URLSearchParams();
    if (mlaUserId && mlaUserId !== MlaAuthService.PLACEHOLDER_USER_ID) params.append('userId', mlaUserId);
    if (loanId) params.append('loanId', loanId);
    if (borrowerId) params.append('borrowerId', borrowerId);

    const visitorId = this.generateVisitorId();
    if (visitorId) params.append('visitorId', visitorId);

    const mlaDomainLink = this.env.appSettings.mlaWebUrl;
    if (mlaDomainLink) params.append('mlaDomainLink', mlaDomainLink);

    const url = `${this.mlaApiUrl}api/v1/session/sso?${params.toString()}`;

    try {
      const response = await firstValueFrom(this.http.get<{ success: boolean; data: string }>(url));

      if (response?.success && response.data) {
        return response.data;
      } else {
        this.loggingService.logWarning('[MlaAuthService] Failed to get SSO token', {
          success: String(response?.success),
        });
        return null;
      }
    } catch (error) {
      this.loggingService.logException(error, '[MlaAuthService] Error getting SSO token:');
      return null;
    }
  }

  async getSsoTokenForMlaRedirect(loanId?: string): Promise<string | null> {
    if (!this.userExistsInMla()) {
      return null;
    }

    const token = this.bearerToken;
    if (!token) {
      this.loggingService.logWarning('[MlaAuthService] No MLA token available for SSO token generation (MLA redirect)');
      return null;
    }

    const params = new URLSearchParams();
    const mlaUserId = this.userId;
    if (mlaUserId) params.append('userId', mlaUserId);
    if (loanId) {
      params.append('loanId', loanId);
      const mlaBorrowerId = this.borrowerId;
      if (mlaBorrowerId) params.append('borrowerId', mlaBorrowerId);
    }

    const visitorId = this.generateVisitorId();
    if (visitorId) params.append('visitorId', visitorId);

    const mlaDomainLink = this.env.appSettings.mlaWebUrl;
    if (mlaDomainLink) params.append('mlaDomainLink', mlaDomainLink);

    const url = `${this.mlaApiUrl}api/v1/session/sso?${params.toString()}`;

    try {
      const response = await firstValueFrom(this.http.get<{ success: boolean; data: string }>(url));

      if (response?.success && response.data) {
        return response.data;
      } else {
        this.loggingService.logWarning('[MlaAuthService] Failed to get SSO token for MLA redirect', {
          success: String(response?.success),
        });
        return null;
      }
    } catch (error) {
      this.loggingService.logException(error, '[MlaAuthService] Error getting SSO token for MLA redirect:');
      return null;
    }
  }

  async ensureMlaSession(): Promise<boolean> {
    if (!this.userExistsInMla()) {
      return false;
    }

    if (!this.hasEpsAccount()) {
      return false;
    }

    const existingToken = sessionStorage.getItem(this.STORAGE_KEYS.epsToken);
    const existingUserId = sessionStorage.getItem(this.STORAGE_KEYS.userId);

    if (existingToken && existingUserId) {
      return true;
    }

    const b2cIdToken = this.authService.getB2cIdToken();
    if (!b2cIdToken) {
      this.loggingService.logWarning('[MlaAuthService] No B2C ID token available for MLA authentication');
      return false;
    }

    const email = this.authService.getUserEmail();
    if (!email) {
      this.loggingService.logWarning('[MlaAuthService] No user email available for MLA authentication');
      return false;
    }

    if (!this.userStatusLoaded) {
      try {
        await this.getUserStatus(email, this.resolveStatusPartnerId());
      } catch {
        this.loggingService.logWarning('[MlaAuthService] Could not refresh MLA user status before authentication');
      }

      if (!this.userExistsInMla() || !this.hasEpsAccount()) {
        return false;
      }
    }

    const authResult = this.userStatusLoaded
      ? await this.authenticateOrRegisterB2CUser(b2cIdToken, email)
      : await this.authenticateB2CUser(b2cIdToken, email);
    if (!authResult) return false;

    const storedUserId = sessionStorage.getItem(this.STORAGE_KEYS.userId);
    if (!storedUserId) {
      this.loggingService.logWarning('[MlaAuthService] authenticateB2CUser succeeded but no userId stored');
    }

    return true;
  }

  async sendVerificationCode(phoneNumber: string, channel: 'sms' | 'call'): Promise<boolean> {
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    const url = `${this.mlaApiUrl}api/v1/verify/user/send-verification?phoneNumber=${digitsOnly}&channel=${channel}`;

    const headers: Record<string, string> = {
      HostInfo: this.buildHostInfo(),
    };

    try {
      const response = await firstValueFrom(this.http.post<{ success: boolean }>(url, {}, { headers }));
      return response?.success === true;
    } catch (error) {
      this.loggingService.logException(error, '[MlaAuthService] Failed to send verification code:');
      return false;
    }
  }

  async verifyCode(
    phoneNumber: string,
    code: string,
    rememberDevice = false,
    email?: string,
  ): Promise<{ success: boolean; message?: string }> {
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    const url = `${this.mlaApiUrl}api/v1/verify/user/check-verification?phoneNumber=${digitsOnly}&code=${code}`;

    const headers: Record<string, string> = {
      HostInfo: this.buildHostInfo(),
    };

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; message?: string }>(url, {}, { headers }),
      );

      const isSuccess = response?.success === true;

      email = email || sessionStorage.getItem('pending_user_email') || '';
      if (isSuccess && rememberDevice && email) {
        this.storeRememberedDevice(email);
      }

      return { success: isSuccess, message: response?.message };
    } catch (error) {
      this.loggingService.logException(error, '[MlaAuthService] Failed to verify code:');
      return { success: false, message: 'Verification failed. Please try again.' };
    }
  }

  private storeRememberedDevice(email: string): void {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 7);

    const newEntry = {
      email,
      expiry: expiryDate.getTime(),
    };

    let deviceList = this.getRememberedDeviceList();

    deviceList = deviceList.filter((device) => device.email !== email);

    deviceList = deviceList.filter((device) => Date.now() <= device.expiry);

    deviceList.push(newEntry);

    const encryptedData = this.encryptDeviceData(JSON.stringify(deviceList));
    localStorage.setItem('mla_remembered_device', encryptedData);
  }

  private getRememberedDeviceList(): Array<{ email: string; expiry: number }> {
    const encryptedData = localStorage.getItem('mla_remembered_device');
    if (!encryptedData) {
      return [];
    }

    try {
      const decryptedData = this.decryptDeviceData(encryptedData);
      const parsed = JSON.parse(decryptedData);

      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.email) {
        return [parsed];
      }

      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  isDeviceRemembered(email: string): boolean {
    const deviceList = this.getRememberedDeviceList();

    if (deviceList.length === 0) {
      return false;
    }

    const deviceEntry = deviceList.find((device) => device.email === email);

    if (!deviceEntry) {
      return false;
    }

    if (Date.now() > deviceEntry.expiry) {
      this.removeExpiredDevices();
      return false;
    }

    return true;
  }

  private removeExpiredDevices(): void {
    let deviceList = this.getRememberedDeviceList();
    const originalLength = deviceList.length;

    deviceList = deviceList.filter((device) => Date.now() <= device.expiry);

    if (deviceList.length !== originalLength) {
      if (deviceList.length === 0) {
        localStorage.removeItem('mla_remembered_device');
      } else {
        const encryptedData = this.encryptDeviceData(JSON.stringify(deviceList));
        localStorage.setItem('mla_remembered_device', encryptedData);
      }
    }
  }

  clearRememberedDevice(): void {
    localStorage.removeItem('mla_remembered_device');
  }

  clearRememberedDeviceForEmail(email: string): void {
    let deviceList = this.getRememberedDeviceList();
    deviceList = deviceList.filter((device) => device.email !== email);

    if (deviceList.length === 0) {
      localStorage.removeItem('mla_remembered_device');
    } else {
      const encryptedData = this.encryptDeviceData(JSON.stringify(deviceList));
      localStorage.setItem('mla_remembered_device', encryptedData);
    }
  }

  private encryptDeviceData(data: string): string {
    const reversed = data.split('').reverse().join('');
    return btoa(reversed);
  }

  private decryptDeviceData(encryptedData: string): string {
    const decoded = atob(encryptedData);
    return decoded.split('').reverse().join('');
  }
}
